import { ApiError, apiSend } from "../../lib/customFetch";
import { z } from "zod";

/**
 * Offline-kø for stemplinger (kontrakt: docs/decisions/0005-clockin-api.md).
 * Stemplinger gemmes i localStorage med clientId + ENHEDENS tidsstempel
 * (genereret ved tryk, ikke ved afsendelse) og synkes i rækkefølge ved
 * online-event og app-start. Serverens idempotens (clientId) gør
 * gensendelse ufarlig.
 *
 * Transiente netværks-/serverfejl beholder køen til automatisk retry. Permanente
 * 4xx-fejl flytter den afviste hændelse og resten af den afhængige kø til en
 * vedvarende "kræver hjælp"-liste, så intet lokalt tidsstempel tabes lydløst.
 */

const QUEUE_KEY = "appsmeden_stemple_koe";
const ATTENTION_KEY = "appsmeden_stemple_kraever_hjaelp";
const QUARANTINE_KEY = "appsmeden_stemple_karantaene";
const LEGACY_STORAGE_KEYS = [QUEUE_KEY, ATTENTION_KEY, QUARANTINE_KEY] as const;
const queueScopeSchema = z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/);
let activeQueueScope: string | null = null;
const STORAGE_VERSION = 1 as const;
export const MAX_AUTO_RETRY_ATTEMPTS = 5;
const RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 60_000] as const;

export interface StampRetryState {
  attempts: number;
  lastAttemptAt: string;
  lastError: string;
  nextAttemptAt?: string | undefined;
}

export interface QueuedStamp {
  clientId: string;
  type: "in" | "out";
  timestamp: string;
  note?: string | undefined;
  // M-11: valgfrit projekt ved indstempling. Gamle køede elementer uden
  // feltet tolereres (spread nedenfor), så opgraderinger ikke taber køen.
  projektId?: string | undefined;
  /** Lokal tofasemarkør; sendes aldrig til API'et. */
  attentionClientId?: string | undefined;
  /** Lokal retrytilstand; sendes aldrig til API'et. */
  retry?: StampRetryState | undefined;
}

export interface FlushRetryStatus extends StampRetryState {
  maxAttempts: typeof MAX_AUTO_RETRY_ATTEMPTS;
  paused: boolean;
}

export interface FlushResult {
  sent: number;
  remaining: number;
  needsAttention: number;
  attentionRemaining: number;
  attentionMessage?: string;
  retry?: FlushRetryStatus;
}

export interface AttentionStamp extends QueuedStamp {
  error: string;
  code: string;
  status?: number | undefined;
  blockedAt: string;
}

export interface QuarantinedStamp {
  id: string;
  source: "pending" | "attention";
  reason:
    | "ugyldig_post"
    | "ukendt_version"
    | "ugyldig_lagerform"
    | "client_id_konflikt";
  isolatedAt: string;
  payload: unknown;
}

const queuedStampSchema = z
  .object({
    clientId: z.uuid(),
    type: z.enum(["in", "out"]),
    timestamp: z.iso.datetime({ offset: true }),
    note: z.string().max(500).optional(),
    projektId: z.uuid().optional(),
    attentionClientId: z.uuid().optional(),
    retry: z
      .object({
        attempts: z.number().int().min(1).max(MAX_AUTO_RETRY_ATTEMPTS),
        lastAttemptAt: z.iso.datetime({ offset: true }),
        lastError: z.string().min(1).max(500),
        nextAttemptAt: z.iso.datetime({ offset: true }).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

const attentionStampSchema = z
  .object({
    clientId: z.uuid(),
    type: z.enum(["in", "out"]),
    timestamp: z.iso.datetime({ offset: true }),
    note: z.string().max(500).optional(),
    projektId: z.uuid().optional(),
    error: z.string().min(1),
    code: z.string().min(1).max(100),
    status: z.number().int().min(400).max(499).optional(),
    blockedAt: z.iso.datetime({ offset: true }),
  })
  .strict();

const storageEnvelopeSchema = z
  .object({
    version: z.literal(STORAGE_VERSION),
    items: z.array(z.unknown()),
  })
  .strict();

const quarantineEnvelopeSchema = z
  .object({
    version: z.literal(STORAGE_VERSION),
    items: z.array(
      z
        .object({
          id: z.uuid(),
          source: z.enum(["pending", "attention"]),
          reason: z.enum([
            "ugyldig_post",
            "ukendt_version",
            "ugyldig_lagerform",
            "client_id_konflikt",
          ]),
          isolatedAt: z.iso.datetime({ offset: true }),
          payload: z.unknown(),
        })
        .strict(),
    ),
  })
  .strict();

type QueueSource = QuarantinedStamp["source"];
type IsolationReason = QuarantinedStamp["reason"];

function storageKey(base: string, scope: string): string {
  return `${base}:${scope}`;
}

/**
 * Bind den lokale kø til den autentificerede medarbejder, før den læses.
 * Uscope'de pre-release-data må aldrig tilskrives den aktuelle bruger.
 */
export function selectStampQueueScope(scope: string): void {
  activeQueueScope = queueScopeSchema.parse(scope);
  for (const key of LEGACY_STORAGE_KEYS) localStorage.removeItem(key);
}

function requireQueueScope(): string {
  if (activeQueueScope === null) {
    throw new Error("Stempelkøen er ikke knyttet til en medarbejder.");
  }
  return activeQueueScope;
}

function envelope<T>(items: T[]): { version: typeof STORAGE_VERSION; items: T[] } {
  return { version: STORAGE_VERSION, items };
}

function readQuarantine(scope: string | null = activeQueueScope): QuarantinedStamp[] {
  if (scope === null) return [];
  try {
    const raw = localStorage.getItem(storageKey(QUARANTINE_KEY, scope));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    const result = quarantineEnvelopeSchema.safeParse(parsed);
    return result.success ? result.data.items : [];
  } catch {
    return [];
  }
}

function isolate(
  scope: string,
  source: QueueSource,
  reason: IsolationReason,
  payloads: unknown[],
): void {
  if (payloads.length === 0) return;
  const isolatedAt = new Date().toISOString();
  const additions: QuarantinedStamp[] = payloads.map((payload) => ({
    id: crypto.randomUUID(),
    source,
    reason,
    isolatedAt,
    payload,
  }));
  localStorage.setItem(
    storageKey(QUARANTINE_KEY, scope),
    JSON.stringify(envelope([...readQuarantine(scope), ...additions])),
  );
}

function sameStamp(a: QueuedStamp, b: QueuedStamp): boolean {
  return (
    a.clientId === b.clientId &&
    a.type === b.type &&
    a.timestamp === b.timestamp &&
    a.note === b.note &&
    a.projektId === b.projektId
  );
}

function loadQueue<T extends QueuedStamp>(
  baseKey: string,
  source: QueueSource,
  schema: z.ZodType<T>,
  scope: string | null = activeQueueScope,
): T[] {
  if (scope === null) return [];
  const key = storageKey(baseKey, scope);
  const raw = localStorage.getItem(key);
  if (raw === null) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    isolate(scope, source, "ugyldig_lagerform", [raw]);
    localStorage.setItem(key, JSON.stringify(envelope([])));
    return [];
  }

  let candidates: unknown[];
  let shouldRewrite = false;
  if (Array.isArray(parsed)) {
    // Version 0 var en rå array. Den migreres additivt ved første læsning.
    candidates = parsed;
    shouldRewrite = true;
  } else {
    const stored = storageEnvelopeSchema.safeParse(parsed);
    if (!stored.success) {
      const version =
        typeof parsed === "object" && parsed !== null && "version" in parsed
          ? (parsed as { version?: unknown }).version
          : undefined;
      isolate(
        scope,
        source,
        version === undefined || version === STORAGE_VERSION
          ? "ugyldig_lagerform"
          : "ukendt_version",
        [parsed],
      );
      localStorage.setItem(key, JSON.stringify(envelope([])));
      return [];
    }
    candidates = stored.data.items;
  }

  const valid: T[] = [];
  const invalid: unknown[] = [];
  const conflicts: unknown[] = [];
  for (const candidate of candidates) {
    const result = schema.safeParse(candidate);
    if (!result.success) {
      invalid.push(candidate);
      shouldRewrite = true;
      continue;
    }
    const existing = valid.find((stamp) => stamp.clientId === result.data.clientId);
    if (existing !== undefined) {
      shouldRewrite = true;
      if (!sameStamp(existing, result.data)) conflicts.push(candidate);
      continue;
    }
    valid.push(result.data);
  }

  isolate(scope, source, "ugyldig_post", invalid);
  isolate(scope, source, "client_id_konflikt", conflicts);
  if (shouldRewrite) localStorage.setItem(key, JSON.stringify(envelope(valid)));
  return valid;
}

export function pendingStamps(): QueuedStamp[] {
  return loadQueue(QUEUE_KEY, "pending", queuedStampSchema);
}

export function attentionStamps(): AttentionStamp[] {
  return loadQueue<AttentionStamp>(ATTENTION_KEY, "attention", attentionStampSchema);
}

export function quarantinedStamps(): QuarantinedStamp[] {
  return readQuarantine();
}

function save(scope: string, queue: QueuedStamp[]): void {
  localStorage.setItem(storageKey(QUEUE_KEY, scope), JSON.stringify(envelope(queue)));
}

function saveAttention(scope: string, queue: AttentionStamp[]): void {
  localStorage.setItem(
    storageKey(ATTENTION_KEY, scope),
    JSON.stringify(envelope(queue)),
  );
}

function isTransientApiError(error: ApiError): boolean {
  return (
    error.status === 408 ||
    error.status === 425 ||
    error.status === 429 ||
    error.status >= 500
  );
}

function queuedPart(stamp: AttentionStamp): QueuedStamp {
  return {
    clientId: stamp.clientId,
    type: stamp.type,
    timestamp: stamp.timestamp,
    ...(stamp.note === undefined ? {} : { note: stamp.note }),
    ...(stamp.projektId === undefined ? {} : { projektId: stamp.projektId }),
  };
}

function retryStatus(stamp: QueuedStamp | undefined): FlushRetryStatus | undefined {
  if (stamp?.retry === undefined) return undefined;
  return {
    ...stamp.retry,
    maxAttempts: MAX_AUTO_RETRY_ATTEMPTS,
    paused: stamp.retry.attempts >= MAX_AUTO_RETRY_ATTEMPTS,
  };
}

function transientMessage(error: unknown): string {
  const fallback = "Der er ingen forbindelse lige nu.";
  const message = error instanceof ApiError ? error.message.trim() : fallback;
  return (message.length === 0 ? fallback : message).slice(0, 500);
}

function recordTransientFailure(
  scope: string,
  rest: QueuedStamp[],
  error: unknown,
): void {
  const stamp = rest[0];
  if (stamp === undefined) return;
  const attempts = Math.min(
    (stamp.retry?.attempts ?? 0) + 1,
    MAX_AUTO_RETRY_ATTEMPTS,
  );
  const lastAttemptAt = new Date().toISOString();
  const delay = RETRY_DELAYS_MS[attempts - 1];
  rest[0] = {
    ...stamp,
    retry: {
      attempts,
      lastAttemptAt,
      lastError: transientMessage(error),
      ...(delay === undefined
        ? {}
        : { nextAttemptAt: new Date(Date.now() + delay).toISOString() }),
    },
  };
  // Retrymetadata skrives uden at rydde kilden. Ved quota-fejl består den
  // hidtidige kø uændret og UI'et viser den vedvarende lagerfejl.
  save(scope, rest);
}

function resetRetryState(scope: string): void {
  const pending = loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope);
  if (!pending.some((stamp) => stamp.retry !== undefined)) return;
  save(
    scope,
    pending.map(({ retry: _retry, ...stamp }) => stamp),
  );
}

export function retryAttentionStamp(clientId: string): boolean {
  const scope = requireQueueScope();
  const attention = loadQueue(ATTENTION_KEY, "attention", attentionStampSchema, scope);
  const stamp = attention.find((item) => item.clientId === clientId);
  if (stamp === undefined) return false;
  const queued: QueuedStamp = {
    ...queuedPart(stamp),
    attentionClientId: clientId,
  };
  const pending = loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope);
  const existing = pending.find((item) => item.clientId === clientId);
  if (existing !== undefined && !sameStamp(existing, queued)) return false;
  // Køskrivningen kommer først: ved lagerfejl består dead-letter-posten.
  save(
    scope,
    existing === undefined
      ? [...pending, queued]
      : pending.map((item) => (item.clientId === clientId ? queued : item)),
  );
  saveAttention(scope, attention.filter((item) => item.clientId !== clientId));
  return true;
}

export function replaceAttentionStamp(
  clientId: string,
  replacement: Pick<QueuedStamp, "timestamp"> &
    Partial<Pick<QueuedStamp, "note" | "projektId">>,
): boolean {
  const scope = requireQueueScope();
  const attention = loadQueue(ATTENTION_KEY, "attention", attentionStampSchema, scope);
  const stamp = attention.find((item) => item.clientId === clientId);
  if (stamp === undefined) return false;
  const hasReplacementNote = Object.prototype.hasOwnProperty.call(
    replacement,
    "note",
  );
  const note = hasReplacementNote ? replacement.note : stamp.note;
  const projektId = replacement.projektId ?? stamp.projektId;
  const next: QueuedStamp = {
    clientId: crypto.randomUUID(),
    type: stamp.type,
    timestamp: replacement.timestamp,
    ...(note === undefined || note.trim() === "" ? {} : { note }),
    ...(projektId === undefined ? {} : { projektId }),
    attentionClientId: clientId,
  };
  if (!queuedStampSchema.safeParse(next).success) return false;
  // Som ved retry skrives den nye post først; gammel dead-letter fjernes sidst.
  save(scope, [
    ...loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope),
    next,
  ]);
  saveAttention(scope, attention.filter((item) => item.clientId !== clientId));
  return true;
}

export function dismissQuarantinedStamp(id: string): void {
  const scope = requireQueueScope();
  localStorage.setItem(
    storageKey(QUARANTINE_KEY, scope),
    JSON.stringify(
      envelope(readQuarantine(scope).filter((item) => item.id !== id)),
    ),
  );
}

export function enqueueStamp(stamp: QueuedStamp): void {
  const scope = requireQueueScope();
  const parsed = queuedStampSchema.safeParse(stamp);
  if (!parsed.success) {
    isolate(scope, "pending", "ugyldig_post", [stamp]);
    return;
  }
  const pending = loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope);
  const existing = pending.find((queued) => queued.clientId === parsed.data.clientId);
  if (existing === undefined) {
    save(scope, [...pending, parsed.data]);
  } else if (!sameStamp(existing, parsed.data)) {
    isolate(scope, "pending", "client_id_konflikt", [stamp]);
  }
}

const flushingScopes = new Set<string>();

export async function flushStampQueue(): Promise<FlushResult> {
  return flushStampQueueFor(requireQueueScope());
}

async function flushStampQueueFor(scope: string): Promise<FlushResult> {
  if (flushingScopes.has(scope)) {
    const pending = loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope);
    return {
      sent: 0,
      remaining: pending.length,
      needsAttention: 0,
      attentionRemaining: loadQueue(
        ATTENTION_KEY,
        "attention",
        attentionStampSchema,
        scope,
      ).length,
      ...(retryStatus(pending[0]) === undefined
        ? {}
        : { retry: retryStatus(pending[0])! }),
    };
  }
  flushingScopes.add(scope);
  try {
    const rest = loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope);
    let sent = 0;
    let needsAttention = 0;
    let attentionMessage: string | undefined;

    while (rest.length > 0) {
      const stamp = rest[0]!;
      const path =
        stamp.type === "in" ? "/api/clockin/clock-in" : "/api/clockin/clock-out";
      try {
        await apiSend("POST", path, {
          clientId: stamp.clientId,
          timestamp: stamp.timestamp,
          ...(stamp.note !== undefined ? { note: stamp.note } : {}),
          ...(stamp.projektId !== undefined ? { projektId: stamp.projektId } : {}),
        });
        sent++;
        const resolvedAttentionId = stamp.attentionClientId ?? stamp.clientId;
        const attention = loadQueue(
          ATTENTION_KEY,
          "attention",
          attentionStampSchema,
          scope,
        );
        if (attention.some((item) => item.clientId === resolvedAttentionId)) {
          // Fjern dead-letter før køposten: hvis næste write fejler, er en
          // server-idempotent replay sikrere end en forældet handlingspost.
          saveAttention(
            scope,
            attention.filter((item) => item.clientId !== resolvedAttentionId),
          );
        }
        rest.shift();
        save(scope, rest);
      } catch (err) {
        if (err instanceof ApiError) {
          if (isTransientApiError(err)) {
            recordTransientFailure(scope, rest, err);
            break;
          } else {
            // Et efterfølgende clock-out kan afhænge af den afviste clock-in.
            // Bevar derfor hele resten samlet til synlig manuel afklaring.
            const blockedAt = new Date().toISOString();
            const code = err.code ?? `http_${String(err.status)}`;
            const blocked = rest.splice(0).map((queued, index) => {
              const {
                attentionClientId: _attentionClientId,
                retry: _retry,
                ...publicStamp
              } = queued;
              return {
                ...publicStamp,
                code,
                ...(err.status >= 400 && err.status < 500
                  ? { status: err.status }
                  : {}),
                error:
                  index === 0
                    ? err.message
                    : `Afventer afklaring efter: ${err.message}`,
                blockedAt,
              };
            });
            const blockedIds = new Set(blocked.map((item) => item.clientId));
            // Hjælpelisten skrives før køen ryddes. Hvis køskrivningen derefter
            // fejler, findes hændelserne i begge lagre og kan deduplikeres ved
            // næste forsøg; det er bedre end at miste dem fra begge.
            saveAttention(scope, [
              ...loadQueue(
                ATTENTION_KEY,
                "attention",
                attentionStampSchema,
                scope,
              ).filter(
                (item) => !blockedIds.has(item.clientId),
              ),
              ...blocked,
            ]);
            save(scope, rest);
            needsAttention += blocked.length;
            attentionMessage = err.message;
            break;
          }
        } else {
          recordTransientFailure(scope, rest, err);
          break;
        }
      }
    }
    return {
      sent,
      remaining: rest.length,
      needsAttention,
      attentionRemaining: loadQueue(
        ATTENTION_KEY,
        "attention",
        attentionStampSchema,
        scope,
      ).length,
      ...(attentionMessage === undefined ? {} : { attentionMessage }),
      ...(retryStatus(rest[0]) === undefined
        ? {}
        : { retry: retryStatus(rest[0])! }),
    };
  } finally {
    flushingScopes.delete(scope);
  }
}

export interface StampSyncController {
  flushNow(options?: { resetRetry?: boolean }): void;
  dispose(): void;
}

/** Registrér online-sync, afgrænset backoff og et flush ved app-start. */
export function initStampSync(
  onFlushed: (result: FlushResult) => void,
  onError: (error: unknown) => void = () => undefined,
  onSending: (count: number) => void = () => undefined,
): StampSyncController {
  const scope = requireQueueScope();
  let timer: number | undefined;
  let disposed = false;
  let running = false;
  let rerun = false;
  let rerunResetsRetry = false;

  const clearTimer = () => {
    if (timer !== undefined) window.clearTimeout(timer);
    timer = undefined;
  };

  const run = (resetRetry: boolean) => {
    clearTimer();
    if (disposed) return;
    if (running) {
      rerun = true;
      rerunResetsRetry ||= resetRetry;
      return;
    }
    running = true;
    try {
      if (resetRetry) resetRetryState(scope);
      const pending = loadQueue(QUEUE_KEY, "pending", queuedStampSchema, scope);
      const existingRetry = retryStatus(pending[0]);
      if (!resetRetry && existingRetry?.paused === true) {
        onFlushed({
          sent: 0,
          remaining: pending.length,
          needsAttention: 0,
          attentionRemaining: loadQueue(
            ATTENTION_KEY,
            "attention",
            attentionStampSchema,
            scope,
          ).length,
          retry: existingRetry,
        });
        running = false;
        onSending(0);
        return;
      }
      const count = pending.length;
      if (count > 0) onSending(count);
    } catch (error) {
      running = false;
      onError(error);
      return;
    }

    void flushStampQueueFor(scope)
      .then((result) => {
        if (disposed) return;
        onFlushed(result);
        const nextAttemptAt = result.retry?.nextAttemptAt;
        if (result.retry?.paused !== true && nextAttemptAt !== undefined) {
          const delay = Math.max(0, new Date(nextAttemptAt).getTime() - Date.now());
          timer = window.setTimeout(() => run(false), delay);
        }
      })
      .catch((error: unknown) => {
        if (!disposed) onError(error);
      })
      .finally(() => {
        if (disposed) return;
        running = false;
        onSending(0);
        if (rerun) {
          const shouldReset = rerunResetsRetry;
          rerun = false;
          rerunResetsRetry = false;
          run(shouldReset);
        }
      });
  };

  const onlineHandler = () => run(true);
  window.addEventListener("online", onlineHandler);
  run(false);
  return {
    flushNow: (options) => run(options?.resetRetry === true),
    dispose: () => {
      disposed = true;
      clearTimer();
      window.removeEventListener("online", onlineHandler);
    },
  };
}
